export const SUPPORTED_LANGS = ['en', 'es', 'ar'];//Add new lanaguae here

// textObj: { en: "...", es: "...", ar: "" }
export const getFirstSupportedText = (textObj = {}, supportedLangs = [], preferred = 'en') => {
  if (!textObj || typeof textObj !== 'object') return null;
  // prefer preferred language if present & non-empty
  const pref = (textObj[preferred] || '').trim();
  if (pref) return pref;

  // otherwise return the first supported non-empty language
  for (const lang of supportedLangs) {
    const v = (textObj[lang] || '').trim();
    if (v) return v;
  }

  // fallback to any non-empty value (defensive)
  for (const k of Object.keys(textObj)) {
    if ((textObj[k] || '').trim()) return textObj[k].trim();
  }
  return null;
};

// Keep only supported languages with non-empty values for text/options/items
export const sanitizeQuestionForSupportedLanguages = (question, supportedLangs = []) => {
  const q = { ...question };

  // sanitize text
  q.text = {};
  for (const lang of supportedLangs) {
    if ((question.text?.[lang] || '').trim()) q.text[lang] = question.text[lang].trim();
  }

  // sanitize options (keep arrays only if non-empty)
  q.options = {};
  for (const lang of supportedLangs) {
    const arr = Array.isArray(question.options?.[lang]) ? question.options[lang].filter(o => String(o).trim()) : [];
    q.options[lang] = arr;
  }

  // sanitize items (for matrix-type)
  if (Array.isArray(question.items)) {
    q.items = question.items.map(item => {
      const newItem = { ...item, text: {} };
      for (const lang of supportedLangs) {
        const v = (item.text?.[lang] || '').trim();
        if (v) newItem.text[lang] = v;
      }
      return newItem;
    }).filter(it => Object.keys(it.text).length > 0); // drop rows with no text
  }

  return q;
};

// Flatten a raw multi-language question ({ text:{en,ar}, options:{en:[],ar:[]} })
// into the single-language shape the passenger renderer (survey-question.js) expects
// ({ text:"…", options:[…] }). Mirrors what the backend paxSurvey endpoint returns,
// so an unsaved draft can be previewed client-side with no network call.
export const flattenQuestionForLanguage = (question = {}, lang = 'en') => ({
  ...question,
  text: getFirstSupportedText(question.text, SUPPORTED_LANGS, lang) || '',
  options: question.options?.[lang] ?? question.options?.en ?? [],
  items: Array.isArray(question.items)
    ? question.items.map(item => ({
        ...item,
        text: getFirstSupportedText(item.text, SUPPORTED_LANGS, lang) || '',
      }))
    : question.items,
  scaleLabels: question.scaleLabels, // renderer already reads scaleLabels[lang] with an en fallback
});

export const flattenSurveyForLanguage = (survey, lang = 'en') =>
  (survey?.questions || []).map(question => flattenQuestionForLanguage(question, lang));

const translations = {
  // Survey intro screen
  title: {
    en: "Passenger survey",
    es: "Encuesta para pasajeros",
    ar: "استبيان المسافرين"
  },
  subTitle: {
    en: "This survey should take you an estimated 3 mins to complete and will help all airports improve the service going forward.",
    es: "Esta encuesta le tomará aproximadamente 3 minutos y ayudará a todos los aeropuertos a mejorar el servicio en el futuro.",
    ar: "من المفترض أن يستغرق إكمال هذا الاستطلاع حوالي 3 دقائق وسيساعد جميع المطارات على تحسين الخدمة في المستقبل."
  },
  startBtn: {
    en: "Start Survey",
    es: "Comenzar encuesta",
    ar: "بدء الاستبيان"
  },
  surveyCompleted: {
    en: "The survey for this passenger has already been submitted",
    es: "La encuesta para este pasajero ya ha sido enviada",
    ar: "لقد تم تقديم المراجعة لهذا الراكب بالفعل"
  },

  // Survey questions navigation
  nextQuestion: {
    en: "Next Question",
    es: "Siguiente Pregunta",
    ar: "السؤال التالي"
  },
  submitBtn: {
    en: "Complete Survey",
    es: "Completar Encuesta",
    ar: "إرسال الاستبيان"
  },
  finalBtn: {
    en: "Survey Completed",
    es: "Encuesta Completada",
    ar: "انتهى الاستبيان"
  },
  finalPhrase: {
    en: "Thank you for completing the survey and for providing us with useful feedback to help improve the passenger journeys",
    es: "Gracias por completar la encuesta y por brindarnos comentarios útiles para ayudar a mejorar la experiencia de los pasajeros",
    ar: "نشكرك على إكمال الاستطلاع وتزويدنا بملاحظات مفيدة للمساعدة في تحسين رحلات الركاب"
  },

  // Question display
  question: {
    en: "Question",
    es: "Pregunta",
    ar: "السؤال"
  },
  optional: {
    en: "Optional",
    es: "Opcional",
    ar: "اختياري"
  },
  pleaseSelectAnswer: {
    en: "Please select an answer",
    es: "Por favor seleccione una respuesta",
    ar: "يرجى اختيار إجابة"
  },
  pleaseAnswerAllItems: {
    en: "Please answer all items",
    es: "Por favor responda todos los elementos",
    ar: "يرجى الإجابة على جميع العناصر"
  },

  // Comment placeholders
  addComment: {
    en: "Add Your Comment",
    es: "Añade tu comentario",
    ar: "أضف تعليقك.."
  },

  // Matrix scale labels
  scaleLabels: {
    en: ["Excellent", "Very Good", "Good", "Fair", "Bad"],
    es: ["Excelente", "Muy bien", "Bien", "Regular", "Malo"],
    ar: ["ممتاز", "جيد جدًا", "جيد", "متوسط", "سيء"]
  }
};

// preferred: pass appLanguage from app state; fallback to 'en'
export function translate(key, lang = 'en') {
  if (!key) return '';
  const normalized = (lang || 'en').toLowerCase();
  const value = translations[key];

  if (!value) return '';

  // if exact language exists and is non-empty
  if (value[normalized] && String(value[normalized]).trim()) return value[normalized];

  // otherwise try english
  if (value.en && String(value.en).trim()) return value.en;

  // otherwise first non-empty supported
  for (const l of SUPPORTED_LANGS) {
    if (value[l] && String(value[l]).trim()) return value[l];
  }

  // fallback empty
  return '';
}

//#region survey report

export function aggregateOptions(answers, questionId, options) {
  const counts = {};
  options.forEach(o => (counts[o] = 0));

  answers.forEach(({ answers: a }) => {
    const value = a[questionId];
    if (typeof value === "string" && counts[value] !== undefined) {
      counts[value]++;
    }
  });

  return counts;
}

export function aggregateCheckbox(answers, questionId, options) {
  const counts = {};
  options.forEach(o => (counts[o] = 0));

  answers.forEach(({ answers: a }) => {
    const selected = a[questionId]?.options || [];
    selected.forEach(opt => {
      if (counts[opt] !== undefined) counts[opt]++;
    });
  });

  return counts;
}


export function collectCommentsWithDetails(responses, questionId) {
  return responses
    .map((response, index) => {
      const comment = response.answers?.[questionId];
      if (!comment) return null;

      // Handle both string comments and object comments (hybrid-checkbox with comment)
      const commentText = typeof comment === 'string'
        ? comment
        : (comment.comment || JSON.stringify(comment));

      if (!commentText || !commentText.trim()) return null;
      const customId = response.id.replace(/^[A-Z]{3}--/, "");
      return {
        id: customId || `#${index + 1}`,
        response: commentText,
        date: response.date
      };
    })
    .filter(Boolean);
}


export const drawHorizontalBarChart = (doc, startY, data, options = {}) => {
  const {
    startX = 14,
    chartWidth = 180,
    barHeight = 12,
    barSpacing = 8,
    labelWidth = 50,
    showPercentageAxis = true,
    colors = [
      [76, 175, 80],    // Green
      [66, 133, 244],   // Blue
      [251, 192, 45],   // Yellow/Orange
      [77, 182, 172],   // Teal
      [156, 39, 176],   // Purple
      [255, 87, 34],    // Deep Orange
      [0, 150, 136],    // Teal darker
      [121, 134, 203],  // Indigo light
    ]
  } = options;

  const pageWidth = doc.internal.pageSize.getWidth();
  const effectiveChartWidth = Math.min(chartWidth, pageWidth - startX * 2 - labelWidth);
  let y = startY;

  // Find max value for scaling (use 100 for percentages)
  const maxValue = 100;

  // Draw each bar
  data.forEach((item, index) => {
    const barColor = colors[index % colors.length];
    const barWidth = (item.percentage / maxValue) * effectiveChartWidth;

    // Draw label on the left
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.setTextColor(60, 60, 60);

    // Truncate label if too long
    let label = item.name;
    if (label.length > 20) {
      label = label.substring(0, 18) + '..';
    }
    doc.text(label, startX, y + barHeight / 2 + 3);

    // Draw bar background (light gray)
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(startX + labelWidth, y, effectiveChartWidth, barHeight, 2, 2, 'F');

    // Draw bar
    if (barWidth > 0) {
      doc.setFillColor(...barColor);
      doc.roundedRect(startX + labelWidth, y, Math.max(barWidth, 4), barHeight, 2, 2, 'F');
    }

    // Draw percentage value at end of bar
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.setTextColor(80, 80, 80);
    const percentText = `${item.percentage.toFixed(1)}%`;
    const textX = startX + labelWidth + barWidth + 4;
    if (textX + 20 < pageWidth - startX) {
      doc.text(percentText, textX, y + barHeight / 2 + 3);
    }

    y += barHeight + barSpacing;
  });

  // Draw percentage axis at bottom
  if (showPercentageAxis) {
    y += 2;
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(startX + labelWidth, y, startX + labelWidth + effectiveChartWidth, y);

    // Draw axis labels (0%, 20%, 40%, 60%, 80%, 100%)
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(120, 120, 120);

    for (let i = 0; i <= 5; i++) {
      const percent = i * 20;
      const xPos = startX + labelWidth + (percent / 100) * effectiveChartWidth;
      doc.text(`${percent}%`, xPos, y + 8, { align: 'center' });

      // Draw tick mark
      doc.line(xPos, y, xPos, y + 3);
    }

    y += 12;
  }

  return y + 5;
};

export const drawTable = (doc, startY, headers, rows, options = {}) => {
  const {
    startX = 14,
    cellPadding = 3,
    headerBgColor = [66, 139, 202],
    fontSize = 10,
    rowHeight = 8
  } = options;

  const pageWidth = doc.internal.pageSize.getWidth();
  const tableWidth = pageWidth - startX * 2;
  const colWidth = tableWidth / headers.length;

  let y = startY;

  // Draw header background
  doc.setFillColor(...headerBgColor);
  doc.rect(startX, y, tableWidth, rowHeight, 'F');

  // Draw header text
  doc.setFontSize(fontSize);
  doc.setFont(undefined, "bold");
  doc.setTextColor(255, 255, 255);
  headers.forEach((header, i) => {
    doc.text(header, startX + i * colWidth + cellPadding, y + rowHeight - cellPadding);
  });

  y += rowHeight;

  // Draw rows
  doc.setFont(undefined, "normal");
  doc.setTextColor(0, 0, 0);

  rows.forEach((row, rowIndex) => {
    // Alternate row background
    if (rowIndex % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(startX, y, tableWidth, rowHeight, 'F');
    }

    row.forEach((cell, i) => {
      const cellText = String(cell);
      // Truncate long text
      const maxChars = Math.floor((colWidth - cellPadding * 2) / 2);
      const displayText = cellText.length > maxChars ? cellText.substring(0, maxChars - 2) + '..' : cellText;
      doc.text(displayText, startX + i * colWidth + cellPadding, y + rowHeight - cellPadding);
    });

    y += rowHeight;
  });

  // Draw table border
  doc.setDrawColor(200);
  doc.rect(startX, startY, tableWidth, y - startY);

  // Draw column lines
  for (let i = 1; i < headers.length; i++) {
    doc.line(startX + i * colWidth, startY, startX + i * colWidth, y);
  }

  // Draw row lines
  for (let i = 0; i <= rows.length; i++) {
    doc.line(startX, startY + i * rowHeight, startX + tableWidth, startY + i * rowHeight);
  }

  return y + 5; // Return new Y position
};

//#endregion
